import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { teacherToolsService, userDataService } from '../../../../services';

const useTextInputOrLessonSelector = ({ value, onChange, wordLimit = 2000 }) => {
  const { t } = useTranslation(['chatbot']);

  const [mode, setMode] = useState(value?.mode || 'text');
  const [textValue, setTextValue] = useState(value?.text || '');
  const [selectedLesson, setSelectedLesson] = useState(value?.lesson || null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [units, setUnits] = useState([]);
  const [loadingLessonContent, setLoadingLessonContent] = useState(false);
  const [contentError, setContentError] = useState(null);

  const [tocData, setTocData] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);

  const buildFallbackContent = (lesson) => {
    const parts = [];
    if (lesson.title) parts.push(`${t('chatbot:Lesson')}: ${lesson.title}`);
    if (lesson.unitName) parts.push(`${t('chatbot:Unit')}: ${lesson.unitName}`);
    if (lesson.courseName) parts.push(`${t('chatbot:Course')}: ${lesson.courseName}`);
    return parts.join('\n');
  };

  useEffect(() => {
    if (value) {
      setMode(value.mode || 'text');
      setTextValue(value.text || '');
      setSelectedLesson(value.lesson || null);
    }
  }, []);

  // Fetch TOC on mount (pre-fetch so data is ready when dialog opens)
  useEffect(() => {
    let cancelled = false;

    const fetchToc = async () => {
      setLoadingCourses(true);
      try {
        const data = await userDataService.getCoursesToc();
        if (!cancelled) {
          setTocData(data);
          setCourses(data);
        }
      } catch (error) {
        console.error('[TextInputOrLessonSelector] Failed to fetch TOC:', error);
        if (!cancelled) {
          setCourses([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingCourses(false);
        }
      }
    };

    fetchToc();

    return () => {
      cancelled = true;
    };
  }, []);

  const wordCount = useMemo(() => {
    return textValue.trim() ? textValue.trim().split(' ').length : 0;
  }, [textValue]);

  const handleTextChange = useCallback(
    (text) => {
      setMode('text');
      setTextValue(text);
      setSelectedLesson(null);

      const currentWordCount = text.trim() ? text.trim().split(' ').length : 0;
      const isValid = currentWordCount <= wordLimit && currentWordCount > 0;

      onChange({
        mode: 'text',
        text: text,
        lesson: null,
        isValid: isValid,
      });
    },
    [wordLimit, onChange]
  );

  const handleLessonSelect = useCallback(
    async (lesson) => {
      setMode('lesson');
      setTextValue('');
      setIsDialogOpen(false);

      // Set the lesson immediately without word count
      setSelectedLesson({
        ...lesson,
        wordCount: null, // Set to null to indicate loading
      });

      setLoadingLessonContent(true);
      setContentError(null); // Clear any previous errors

      let lessonContent = '';
      let calculatedWordCount = 0;
      const lessonId = lesson?.lessonGuid || lesson?.guid;

      try {
        const response = await teacherToolsService.getLessonContent(lessonId);
        lessonContent = response?.content || response?.text || '';
        lessonContent = lessonContent.trim();

        // Silent fallback: use lesson metadata if content is empty
        if (!lessonContent || lessonContent === '') {
          lessonContent = buildFallbackContent(lesson);
        }
        setTextValue(lessonContent);
        calculatedWordCount = lessonContent ? lessonContent.split(/\s+/).length : 0;
        setSelectedLesson({
          ...lesson,
          wordCount: calculatedWordCount,
        });
      } catch (error) {
        console.error('[TextInputOrLessonSelector] Failed to fetch lesson content:', error);
        // Silent fallback: use lesson metadata
        lessonContent = buildFallbackContent(lesson);
        setTextValue(lessonContent);
        calculatedWordCount = lessonContent ? lessonContent.split(/\s+/).length : 0;
        setSelectedLesson({
          ...lesson,
          wordCount: calculatedWordCount,
        });
      } finally {
        setLoadingLessonContent(false);
      }

      // Pass the calculated word count and isValid in the onChange callback
      const isValid = calculatedWordCount <= wordLimit && calculatedWordCount > 0;

      onChange({
        mode: 'lesson',
        text: lessonContent,
        lesson: {
          ...lesson,
          lessonId: lessonId,
          wordCount: calculatedWordCount,
          content: lessonContent, // Include lesson content for AI quiz generation
        },
        isValid: isValid,
      });
    },
    [onChange, wordLimit]
  );

  const handleRemoveLesson = useCallback(() => {
    setMode('text');
    setSelectedLesson(null);
    setTextValue(''); // Clear the text content
    setContentError(null); // Clear any errors

    // Notify parent - isValid is false since text is empty
    onChange({
      mode: 'text',
      text: '', // Pass empty string instead of textValue
      lesson: null,
      isValid: false,
    });
  }, [onChange]);

  const openDialog = useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  // Synchronous local extraction from cached TOC data instead of async API call
  const loadCourseUnits = useCallback(
    (courseId) => {
      setLoadingUnits(true);
      setUnits([]);

      if (tocData) {
        const course = tocData.find((c) => c.id === courseId);
        const courseUnits = course?.units || [];
        setUnits(courseUnits);
      }

      setLoadingUnits(false);
    },
    [tocData]
  );

  return {
    mode,
    textValue,
    selectedLesson,
    isDialogOpen,
    wordCount,
    wordLimit,
    loadingUnits,
    units,
    loadingLessonContent,
    contentError,
    loadingCourses,

    courses,

    handleTextChange,
    handleLessonSelect,
    handleRemoveLesson,
    openDialog,
    closeDialog,
    loadCourseUnits,
  };
};

export default useTextInputOrLessonSelector;
